<template>
  <div
    :class="[
      $style.root,
      {
        [$style.sizeSmall]: size === 'small',
        [$style.sizeLarge]: size === 'large',
        [$style.paddingLarge]: padding === 'large',
        [$style.headerPlain]: headerAppearance === 'plain',
      },
    ]"
  >
    <div :class="$style.dialogHeader">
      <div :class="$style.actions">
        <SCAIconButton
          v-if="hasCloseIcon"
          :class="$style.closeButton"
          icon="phosphor/x"
          type="ghost"
          :appearance="headerAppearance === 'plain' ? 'secondary' : 'inverted'"
          size="small"
          :title="$t('common.action.close')"
          @click="emit('close')"
        />
        <slot v-if="slots['actions']" name="actions" />
        <SCASearchInput
          v-if="searchTerm !== undefined"
          appearance="border"
          inverted
          :class="$style.search"
          :show-clear-icon="searchTerm.length > 0"
          @clear="emit('update:searchTerm', '')"
        >
          <input
            :value="searchTerm"
            :placeholder="$t('common.action.search')"
            @input="emit('update:searchTerm', ($event.target as HTMLInputElement).value)"
          />
        </SCASearchInput>
      </div>
      <div v-if="slots.dialogHeader" :class="$style.headerSlot"><slot name="dialogHeader" /></div>
      <div v-if="title" :class="$style.title">{{ title }}</div>
    </div>
    <div v-if="slots.contentHeader"><slot name="contentHeader" /></div>
    <div v-if="slots.default" :class="$style.content">
      <slot />
    </div>
    <div v-if="slots.footer" :class="$style.footer"><slot name="footer" /></div>
  </div>
</template>

<script setup lang="ts">
import { useSlots } from "vue";

import SCAIconButton from "./SCAIconButton.vue";
import SCASearchInput from "./SCASearchInput.vue";
import useDialogKeyboard from "./useDialogKeyboard";

const props = withDefaults(
  defineProps<{
    title?: string;
    size?: "small" | "medium" | "large";
    padding?: "medium" | "large";
    hasCloseIcon?: boolean;
    /** Off for dialogs whose body owns Enter (a grid, a multi-field form). */
    confirmOnEnter?: boolean;
    /**
     * `brand` paints the header in the brand colour with inverted content, as every dialog in the
     * app does. `plain` keeps the body's background and puts the title left of the close button —
     * for the confirmation dialog, which reads as a message rather than as a screen.
     */
    headerAppearance?: "brand" | "plain";
    /**
     * Bind with `v-model:search-term` to put a search in the header. Left out, none is rendered —
     * the dialog owns the field itself so its consumers do not each rebuild it.
     */
    searchTerm?: string;
  }>(),
  {
    title: undefined,
    size: "medium",
    padding: "medium",
    hasCloseIcon: true,
    confirmOnEnter: true,
    headerAppearance: "brand",
    searchTerm: undefined,
  },
);
const emit = defineEmits<{
  close: [];
  confirm: [];
  "update:searchTerm": [value: string];
}>();

const slots = useSlots();

useDialogKeyboard({
  onEscape: () => emit("close"),
  onEnter: () => {
    if (props.confirmOnEnter) {
      emit("confirm");
    }
  },
});
</script>

<style module lang="scss">
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../styles/icon.scss" as IconMixins;

.root {
  @include FigmaTypoMixins.typo-body-medium;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-height: 90dvh;
  width: calc(100% - 2 * var(--space-component-padding-medium));
  max-width: 720px;
  display: flex;
  flex-direction: column;
  border-radius: var(--border-radius-100);

  background-color: var(--color-background-default);
  box-shadow:
    0 8px 16px -2px var(--color-coolGreyA-p3),
    0 2px 2px -1px var(--color-coolGreyA-p6);

  &.sizeSmall {
    max-width: 480px;
  }
  &.sizeLarge {
    max-width: 960px;
  }

  --sca-dialog-padding: var(--space-component-padding-medium);

  &.paddingLarge {
    --sca-dialog-padding: var(--space-component-padding-large);
  }

  & > div:not(.dialogHeader) {
    padding: var(--sca-dialog-padding) var(--sca-dialog-padding) 0 var(--sca-dialog-padding);
    &:first-child,
    &:last-child {
      padding-bottom: var(--sca-dialog-padding);
    }
  }

  &.headerPlain {
    border: 1px solid var(--color-border-default);

    > .dialogHeader {
      padding: var(--space-component-padding-large) var(--space-component-padding-large)
        var(--space-component-padding-small);
    }

    > div:not(.dialogHeader) {
      padding: var(--space-component-padding-xSmall) var(--space-component-padding-large);

      &:last-child {
        padding: var(--space-component-padding-large);
      }
    }
  }
}

.dialogHeader {
  @include FigmaTypoMixins.typo-body-medium;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--space-component-gap-xLarge);
  white-space: nowrap;
  height: var(--dimension-550);
  padding: 0 var(--space-component-padding-medium);
  background-color: var(--color-content-brand-primary);
  color: var(--color-content-primary-inverted);
  border-bottom: solid 1px var(--color-border-neutral-subtle);
  border-top-left-radius: var(--border-radius-100);
  border-top-right-radius: var(--border-radius-100);
  flex-shrink: 0;

  .actions {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0 var(--space-component-padding-small);
    flex-shrink: 1;
    min-width: 0;
  }

  .title {
    flex-grow: 1;
    min-width: 0;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .actions input:not([type="checkbox"]) {
    color: var(--color-content-primary-inverted);
  }

  .actions *:has(> input) > svg {
    @include IconMixins.icon-color(var(--color-content-primary-inverted));
  }

  // A filled button brings its own border; only the ghost ones need one drawn here.
  .actions > button[data-type="ghost"]:not(.closeButton) {
    border: 1px solid var(--color-nonSemantic-white-border-subtle);
  }

  .actions > button[data-type="ghost"]:not(.closeButton)[disabled] {
    opacity: 1;
    border-color: var(--color-whiteA-m7);
    color: var(--color-whiteA-m7);

    svg {
      opacity: 1;
    }
  }
}
.content {
  overflow: auto;
}
.closeButton {
  border: none;
}

.headerSlot {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}

.search {
  flex: 0 1 188px;
  width: 188px;
  min-width: 0;
}

.footer {
  display: flex;
  align-items: center;
  gap: 0 var(--space-component-padding-medium);
  flex-shrink: 0;
}

.headerPlain {
  .dialogHeader {
    @include FigmaTypoMixins.typo-header-large;
    line-height: var(--size-xSmall);
    gap: var(--space-component-gap-large);
    height: auto;
    min-height: var(--dimension-550);
    background-color: transparent;
    color: var(--color-content-primary);
    border-bottom: none;

    // The close button belongs after the title here, and only the flex order differs from `brand`.
    .actions {
      order: 1;
    }

    .actions > .closeButton {
      padding: 0;
      min-height: var(--size-xSmall);
      height: var(--size-xSmall);
      width: var(--size-xSmall);
    }

    .actions > button[data-type="ghost"]:not(.closeButton) {
      border: none;
      box-shadow: none;
    }
  }
}
</style>
